import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Platform,
  Linking,
} from 'react-native';
import { activeCalendarIntegrationService } from '../services/activeCalendarIntegrationService';
import { syncUpcomingBusinessAppointmentsToCalendars } from '../services/businessCalendarSyncService';
import type { BusinessOwner } from '../utils/authManager';
import type { CalendarAccount } from '../services/calendarIntegrationService';
import CalendarProviderBrandIcon from './CalendarProviderBrandIcon';

type Provider = 'google' | 'outlook' | 'apple';

const showUserAlert = (title: string, message: string) => {
  if (Platform.OS === 'web') {
    (globalThis as { alert?: (msg: string) => void }).alert?.(`${title}\n\n${message}`);
  } else {
    Alert.alert(title, message);
  }
};

interface CalendarIntegrationModalProps {
  visible: boolean;
  onClose: () => void;
  /** When set (e.g. from business Settings), enables “Sync upcoming” for that business. */
  businessOwner?: BusinessOwner | null;
}

const CalendarIntegrationModal: React.FC<CalendarIntegrationModalProps> = ({
  visible,
  onClose,
  businessOwner,
}) => {
  const [accounts, setAccounts] = useState<CalendarAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const list = await activeCalendarIntegrationService.getConnectedAccounts();
      setAccounts(list);
    } catch (error) {
      console.error('Error loading calendar accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      loadAccounts();
    }
  }, [visible]);

  const accountFor = (provider: Provider): CalendarAccount | undefined =>
    accounts.find(a => a.type === provider);

  const handleConnect = async (provider: Provider) => {
    if (provider === 'apple' && Platform.OS === 'web') {
      showUserAlert(
        'Mobile app only',
        'Apple Calendar connection uses your device’s calendar app. Use the BookAPT iOS or Android app to connect Apple Calendar.',
      );
      return;
    }
    try {
      setConnecting(provider);
      let result;
      switch (provider) {
        case 'google':
          result = await activeCalendarIntegrationService.connectGoogleCalendar();
          break;
        case 'outlook':
          result = await activeCalendarIntegrationService.connectOutlookCalendar();
          break;
        case 'apple':
          result = await activeCalendarIntegrationService.connectAppleCalendar();
          break;
      }
      if (result?.success && result.calendarId !== 'pending') {
        await loadAccounts();
      } else if (result?.error) {
        showUserAlert('Could not connect', result.error);
      }
    } catch (error) {
      console.error(`Error connecting ${provider}:`, error);
      showUserAlert('Could not connect', 'Something went wrong. Please try again.');
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = (provider: Provider) => {
    Alert.alert(
      'Disconnect calendar',
      `Disconnect ${getProviderName(provider)} on this device? Your BookAPT account is unchanged; only local calendar access is removed.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Disconnect',
          style: 'destructive',
          onPress: async () => {
            const ok = await activeCalendarIntegrationService.disconnectCalendar(provider);
            if (ok) await loadAccounts();
          },
        },
      ],
    );
  };

  const handleOpenInBrowser = async (provider: Provider) => {
    const url =
      provider === 'google'
        ? 'https://calendar.google.com'
        : provider === 'outlook'
          ? 'https://outlook.live.com/calendar/'
          : 'https://www.icloud.com/calendar/';
    const supported = await Linking.canOpenURL(url);
    if (supported) await Linking.openURL(url);
  };

  const handleSyncUpcoming = async () => {
    if (!businessOwner?.id) return;
    try {
      setSyncing(true);
      const { created, skipped, errors } = await syncUpcomingBusinessAppointmentsToCalendars(businessOwner, {
        daysAhead: 21,
      });
      const errSample = errors.slice(0, 3).join('\n');
      const more = errors.length > 3 ? `\n… and ${errors.length - 3} more` : '';
      Alert.alert(
        'Sync finished',
        `Events created: ${created}\nAppointments skipped (cancelled, etc.): ${skipped}${
          errors.length ? `\n\nNotes:\n${errSample}${more}` : ''
        }`,
      );
    } catch (e: any) {
      Alert.alert('Sync failed', e?.message || 'Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const getProviderName = (provider: string) => {
    switch (provider) {
      case 'google':
        return 'Google Calendar';
      case 'outlook':
        return 'Microsoft Outlook';
      case 'apple':
        return 'Apple Calendar';
      default:
        return provider;
    }
  };

  const getProviderDescription = (provider: string) => {
    switch (provider) {
      case 'google':
        return 'Gmail / Google Calendar on this device';
      case 'outlook':
        return 'Outlook.com, Microsoft 365, or Hotmail';
      case 'apple':
        return 'iPhone or iPad Calendar (iCloud, etc.)';
      default:
        return 'Calendar';
    }
  };

  const providers: Provider[] = ['google', 'outlook', 'apple'];

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <View style={styles.header}>
            <Text style={styles.title}>Calendar integration</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.content}>
            <Text style={styles.description}>
              Connect a calendar on this device so your bookings appear next to the rest of your schedule. You can run
              a sync to add upcoming appointments from BookAPT.
            </Text>

            {businessOwner?.id && (
              <TouchableOpacity
                style={[styles.syncBanner, syncing && { opacity: 0.7 }]}
                onPress={handleSyncUpcoming}
                disabled={syncing}
              >
                {syncing ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={styles.syncBannerText}>Sync next 3 weeks of appointments to connected calendars</Text>
                )}
              </TouchableOpacity>
            )}

            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#007AFF" />
                <Text style={styles.loadingText}>Loading…</Text>
              </View>
            ) : (
              <View style={styles.integrationsList}>
                {providers.map(provider => {
                  const acc = accountFor(provider);
                  const isConnected = !!acc?.isConnected;
                  const isConnecting = connecting === provider;
                  const appleWebNote = provider === 'apple' && Platform.OS === 'web';

                  return (
                    <View key={provider} style={styles.integrationItem}>
                      <View style={styles.integrationHeader}>
                        <View style={styles.providerIcon}>
                          <CalendarProviderBrandIcon provider={provider} size={28} />
                        </View>
                        <View style={styles.providerInfo}>
                          <Text style={styles.providerName}>{getProviderName(provider)}</Text>
                          <Text style={styles.providerDescription}>
                            {appleWebNote ? 'Use the mobile app to connect' : getProviderDescription(provider)}
                          </Text>
                          {isConnected && acc?.email ? (
                            <Text style={styles.emailHint} numberOfLines={1}>
                              {acc.email}
                            </Text>
                          ) : null}
                        </View>
                        <View style={styles.statusContainer}>
                          {isConnected ? (
                            <View style={styles.connectedStatus}>
                              <Text style={styles.statusText}>Connected</Text>
                            </View>
                          ) : (
                            <View style={styles.disconnectedStatus}>
                              <Text style={styles.statusText}>Not connected</Text>
                            </View>
                          )}
                        </View>
                      </View>

                      <View style={styles.integrationActions}>
                        {isConnected ? (
                          <>
                            <TouchableOpacity style={styles.actionButton} onPress={() => handleOpenInBrowser(provider)}>
                              <Text style={styles.actionButtonText}>Open on web</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={[styles.actionButton, styles.disconnectButton]}
                              onPress={() => handleDisconnect(provider)}
                            >
                              <Text style={[styles.actionButtonText, styles.disconnectButtonText]}>Disconnect</Text>
                            </TouchableOpacity>
                          </>
                        ) : (
                          <TouchableOpacity
                            style={[styles.actionButton, styles.connectButton, appleWebNote && { opacity: 0.5 }]}
                            onPress={() => handleConnect(provider)}
                            disabled={isConnecting || !!appleWebNote}
                          >
                            {isConnecting ? (
                              <ActivityIndicator size="small" color="#FFFFFF" />
                            ) : (
                              <Text style={[styles.actionButtonText, styles.connectButtonText]}>Connect</Text>
                            )}
                          </TouchableOpacity>
                        )}
                      </View>

                      {isConnected && acc?.lastSync ? (
                        <Text style={styles.lastSyncText}>Last updated: {new Date(acc.lastSync).toLocaleString()}</Text>
                      ) : null}
                    </View>
                  );
                })}
              </View>
            )}

            <View style={styles.infoSection}>
              <Text style={styles.infoTitle}>How it works</Text>
              <Text style={styles.infoText}>
                • Connections are stored on this device (same as other calendar apps using OAuth).{'\n'}
                • Google and Outlook open the provider’s sign-in page; finish authorization there.{'\n'}
                • Use “Sync next 3 weeks” to add BookAPT appointments to your calendar. Running sync again may create
                duplicate events until per-event tracking is added.{'\n'}
                • In Settings, turn on “Automatic calendar sync” to add new bookings while BookAPT is open.{'\n'}
                • Yahoo Mail calendar is not built in yet; use Google or Outlook if your mail is linked there.
              </Text>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    width: '90%',
    maxHeight: '85%',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E7',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    flex: 1,
  },
  closeButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#F2F2F7',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 16,
    color: '#8E8E93',
    fontWeight: 'bold',
  },
  content: {
    padding: 20,
  },
  description: {
    fontSize: 15,
    color: '#8E8E93',
    marginBottom: 16,
    lineHeight: 22,
  },
  syncBanner: {
    backgroundColor: '#007AFF',
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginBottom: 16,
    alignItems: 'center',
  },
  syncBannerText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
  },
  loadingContainer: {
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#8E8E93',
  },
  integrationsList: {
    marginBottom: 12,
  },
  integrationItem: {
    backgroundColor: '#F8F9FA',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E5E7',
  },
  integrationHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  providerIcon: {
    marginRight: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  providerInfo: {
    flex: 1,
  },
  providerName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  providerDescription: {
    fontSize: 14,
    color: '#8E8E93',
  },
  emailHint: {
    fontSize: 12,
    color: '#636366',
    marginTop: 4,
  },
  statusContainer: {
    marginLeft: 8,
  },
  connectedStatus: {
    backgroundColor: '#34C759',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  disconnectedStatus: {
    backgroundColor: '#FF9500',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  integrationActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#007AFF',
  },
  connectButton: {
    backgroundColor: '#007AFF',
  },
  disconnectButton: {
    borderColor: '#FF3B30',
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#007AFF',
  },
  connectButtonText: {
    color: '#FFFFFF',
  },
  disconnectButtonText: {
    color: '#FF3B30',
  },
  lastSyncText: {
    fontSize: 12,
    color: '#8E8E93',
    marginTop: 8,
    fontStyle: 'italic',
  },
  infoSection: {
    backgroundColor: '#E3F2FD',
    borderRadius: 12,
    padding: 16,
    marginTop: 8,
    marginBottom: 24,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1976D2',
    marginBottom: 8,
  },
  infoText: {
    fontSize: 14,
    color: '#1976D2',
    lineHeight: 20,
  },
});

export default CalendarIntegrationModal;
